import { Component } from "react";
import * as React from "react";
import { AccordionBox } from "./AccordionBox";
import { AccordionSet } from "./AccordionSet";
import PopupHelp from "../popups/popupHelp";
import I18n from "lib/i18n/i18n";
import { BoxedSelectList } from "./BoxedSelectList";
import { BoxedYesNo } from "./BoxedYesNo";
import SellItemWizard from "../sellItemWizard";
import { InfoAlert, MediumPrimaryButton } from "@worthy-npm/clarity";
import { isMobile } from "helpers/ApplicationHelper";

class AccordionForm extends Component {
    // when updating a LabeledField and clicking the accordion box header instead of hitting enter - 2 things happen:
    //   1. the blur event is fired and causes the value to update and the next accordion box to open.
    //   2. the click event on the header, which toggles the clicked accordion box.
    //   we only want one of them to occur, and since they are different events (blur and click) - stopPropagation will
    //   not help here. So a workaround is to measure the time from the last change, and not change automatically
    //   in close proximity.
    lastFieldFocusChange = 0;
    shouldOpenChild = false;

    constructor(props) {
        super(props);
        this.state = {
            openChild: this.props.options[0].name, // by default open the first child
            errorText: "",
            submitted: false,
            showAlert: Boolean(
                props.location &&
                    props.location.query &&
                    props.location.query.alertMostValuable
            ),
        };

        this.onValueChanged = this.onValueChanged.bind(this);
        this.toggleBox = this.toggleBox.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.openHelp = this.openHelp.bind(this);
        this.openNextChild = this.openNextChild.bind(this);
        this.alertMostValuable = this.alertMostValuable.bind(this);
    }

    renderHeader() {
        return (
            <div className="accordion-form-header">
                <SellItemWizard step={1} />
            </div>
        );
    }

    onValueChanged(key, value) {
        // after the update, we will want to change the open child.
        // since we will update the state in case there are dependencies - we need to maintain a copy
        // so that we will know what the state is going to be.
        let stateCopy = Object.assign({}, this.state);

        // a value has changed. if any other form value depends on the current - initialize it.
        let that = this;
        this.props.options
            .filter((o) => o.dependsOn === key)
            .forEach((x) => {
                // only initialize the dependant if the value has changed.
                if (value != that.state[key]) {
                    that.setState({ [x.name]: undefined });
                    stateCopy[x.name] = undefined;
                }
            });

        // set the new value, and update parent.
        this.setState({ [key]: value });
        stateCopy[key] = value;

        this.props.onValueChanged(key, value, this.openNextChild);

        let currTime = new Date().getTime();
        if (value && currTime - this.lastFieldFocusChange > 200) {
            this.lastFieldFocusChange = currTime;
            this.shouldOpenChild = true;

            // if the field currently updated is the one that was in error - remove the error.
            if (this.state.errorField === this.state.openChild) {
                this.setState({ errorField: undefined, errorText: "" });
            }
        }
    }

    openNextChild() {
        if (this.shouldOpenChild) {
            let index = this.props.options.findIndex(
                (x) => !this.state[x.name] || this.state[x.name] === ""
            );
            let openChild =
                index > -1 ? this.props.options[index].name : undefined;
            setTimeout(
                (() => {
                    this.setState({ openChild });
                }).bind(this),
                50
            );
            this.shouldOpenChild = false;
        }
    }

    buildBox(option) {
        let key = option.name;

        // Get the props from the form options - copy object for updating
        let props = Object.assign(
            {
                value: this.state[key],
                isOpen: this.state.openChild === key,
                errorField:
                    this.state.errorField === option.name ? true : undefined,
            },
            option
        );

        // return the relevant box.
        return (
            <AccordionBox key={key} {...props} toggleBox={this.toggleBox}>
                {React.createElement(
                    option.type,
                    Object.assign({}, props, {
                        onValueChanged: this.onValueChanged,
                    })
                )}
            </AccordionBox>
        );
    }

    toggleBox(name) {
        let currTime = new Date().getTime();
        if (currTime - this.lastFieldFocusChange < 200) {
            return;
        }
        this.lastFieldFocusChange = currTime;

        // if the closing accordion box value was not selected - make sure it's not an empty string.
        // can happen for BoxedSelectList when choosing other and not providing input.
        if (this.state[this.state.openChild] === "") {
            this.setState({ [this.state.openChild]: undefined });
        }

        if (this.state.openChild === name) {
            this.setState({ openChild: undefined });
        } else {
            this.setState({ openChild: name });
        }
    }

    renderOptions() {
        return this.props.options.map((o) => this.buildBox(o));
    }

    getErrorText(optionName) {
        let fullOptions = this.props.options.find((o) => o.name === optionName);
        if (
            fullOptions.type === BoxedSelectList ||
            fullOptions.type === BoxedYesNo
        ) {
            if (fullOptions.hasOther && fullOptions.fillOtherOption) {
                return I18n.getString("submitForm::errorMessage::both", {
                    fieldLabel: fullOptions.label,
                });
            } else {
                return I18n.getString("submitForm::errorMessage::select", {
                    fieldLabel: fullOptions.label,
                });
            }
        } else {
            return I18n.getString("submitForm::errorMessage::enter", {
                fieldLabel: fullOptions.label,
            });
        }
    }

    fireContinueBtnEvent() {}

    submitForm() {
        let details = {};
        for (let option of this.props.options) {
            // if one of the details is not filled - notify on error.
            if (
                option.isRequired &&
                (!this.state[option.name] ||
                    this.state[option.name].length === 0)
            ) {
                this.setState({
                    errorText: this.getErrorText(option.name),
                    errorField: option.name,
                    openChild: option.name,
                });
                return;
            }
            details[option.name] = this.state[option.name];
        }

        this.fireContinueBtnEvent();
        this.setState({ submitted: true });
        // got all details - submit!
        this.props.onSubmit(details);
    }

    openHelp() {
        PopupHelp.initPopup({ single: true, modal: true });
    }

    alertMostValuable() {
        const { showAlert } = this.state;
        const itemType = this.getItemType();
        const alertText = I18n.getString(
            "submitForm::mostValubleAlert"
        ).replace("{itemType}", itemType);
        const alert = showAlert ? (
            <InfoAlert
                text={alertText}
                onClose={() => this.setState({ showAlert: false })}
                marginVsides={"md"}
            />
        ) : null;
        return <div className="jb-alert">{alert}</div>;
    }
    getItemType() {
        let itemType = this.props.name.match(/submit-(.*)/)[1];
        if (!itemType) {
            console.error("itemType not found!");
            itemType = "jewerly";
        }
        return itemType;
    }

    render() {
        return (
            <div className={"accordion-form " + this.props.name}>
                {this.alertMostValuable()}
                {this.renderHeader()}
                <h1 className={"accordion-form-title"}>{this.props.title}</h1>
                <AccordionSet type="submit-watch">
                    {this.renderOptions()}
                </AccordionSet>
                <div className={"accordion-form-error-text"}>
                    {this.state.errorText}
                </div>
                <div className="submit-clarity-button-wrapper accordion-form-submit-wrapper">
                    <MediumPrimaryButton
                        click={(stopAnimation) => {
                            stopAnimation();
                            this.submitForm();
                        }}
                        text={I18n.getString("submitForm::submitButton")}
                        disabled={this.state.submitted}
                        overrideWrapperCSS={{
                            width: "100%",
                            textAlign: "center",
                        }}
                        overrideCSS={{
                            backgroundColor: "#2D5DEC",
                            borderRadius: "3px",
                            width: `${isMobile() ? "100%" : "125px"}`,
                            padding: "11px 22px 12px",
                            font: "500 16px Roboto",
                            marginTop: 0,
                        }}
                    />
                </div>
                <div className={"accordion-form-bottom-text"}>
                    <span>{I18n.getString("submitForm::needHelp")} </span>
                    <span className="link" onClick={this.openHelp}>
                        {I18n.getString("submitForm::clickToHelp")}
                    </span>
                </div>
            </div>
        );
    }
}

export { AccordionForm };
